import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Clock, BarChart3, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { getFavorites, getHistory, removeFavorite } from "@/lib/api";
import { formatCompact, formatRelative } from "@/lib/format";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function Dashboard() {
  const [favorites, setFavorites] = useState([]);
  const [history, setHistory] = useState([]);

  const load = useCallback(() => {
    getFavorites().then(setFavorites).catch(() => {});
    getHistory().then(setHistory).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await removeFavorite(id);
    toast.success("Favorite removed");
    load();
  };

  const StatCard = ({ icon: Icon, label, value }) => (
    <div className="card-flat p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <div className="overline">{label}</div>
      </div>
      <div className="text-4xl font-black tabular-nums">{value}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20">
      <SEO
        title="Creator Dashboard"
        description="Your favorite YouTube channels, analysis history and platform usage statistics."
        path="/dashboard"
      />
      <h1 className="text-4xl font-black tracking-tight">Dashboard</h1>
      <p className="mt-3 text-muted-foreground">Your favorites, history and platform statistics.</p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={Heart} label="Favorites" value={favorites.length} />
        <StatCard icon={Clock} label="Queries" value={history.length} />
        <StatCard icon={BarChart3} label="Last analysis" value={history[0] ? formatRelative(history[0].queried_at) : "—"} />
      </div>

      <Tabs defaultValue="favorites" className="mt-10">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="favorites" data-testid="tab-favorites">Favorites</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="favorites" className="mt-6">
          {favorites.length === 0 ? (
            <div className="card-flat p-12 text-center text-muted-foreground">
              You haven't favorited any channels yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {favorites.map((f) => (
                <div key={f.channel_id} data-testid={`fav-${f.channel_id}`} className="card-flat p-5 flex items-center gap-4">
                  {f.thumbnail && <img src={f.thumbnail} alt={f.title} loading="lazy" className="w-14 h-14 rounded-lg border border-border" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{f.title}</div>
                    <div className="text-xs text-muted-foreground font-mono">{formatCompact(f.subscribers)} subscribers</div>
                  </div>
                  <div className="flex gap-1">
                    <Link to={`/channel/${f.channel_id}`}>
                      <Button size="sm" variant="outline" className="rounded-full">Open</Button>
                    </Link>
                    <Button size="icon" variant="ghost" onClick={() => del(f.channel_id)} data-testid={`fav-remove-${f.channel_id}`} aria-label="Remove favorite">
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {history.length === 0 ? (
            <div className="card-flat p-12 text-center text-muted-foreground">No queries yet.</div>
          ) : (
            <div className="card-flat divide-y divide-border">
              {history.map((h) => (
                <Link
                  key={h.id}
                  to={`/channel/${h.channel_id}`}
                  data-testid={`history-${h.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-secondary/40"
                >
                  {h.thumbnail && <img src={h.thumbnail} alt={h.title} loading="lazy" className="w-10 h-10 rounded-lg border border-border" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{h.title}</div>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{formatRelative(h.queried_at)}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
